/**
 * LE RÉSERVOIR DE MESURES, CÔTÉ BUILDER.
 *
 * Une page de décisions porte ses mesures de deux façons : une liste figée
 * (« déterministe » : ce que font toutes les pages écrites jusqu'ici) ou un
 * tirage dans le réservoir de l'atelier (« probabiliste »). Ce module ne
 * contient que la lecture des documents — le vocabulaire de `api/measure_pool.py`
 * transposé pour l'édition.
 *
 * CE QU'IL NE CALCULE PAS, ET POURQUOI : le coût d'une mesure, sa bande, et le
 * tirage lui-même. Tout cela vient du serveur, par `/pool_preview`. Un aperçu
 * qui recalculerait ici ce que le jeu calcule là-bas finirait par montrer autre
 * chose que ce que les participants verront — c'est précisément ce qu'on
 * demande à un aperçu de ne pas faire. Le prix à payer est un aller-retour ;
 * il est largement inférieur au prix d'un aperçu qui ment.
 */

/** Le réglage de tirage d'une page, ou null si elle porte une liste figée. */
export function selectionOf(page) {
  const selection = page?.content?.selection;
  if (!selection || selection.mode !== "pool") return null;
  return {
    mode: "pool",
    count: Number(selection.count) || 0,
    minPerBand: Object.fromEntries(
      Object.entries(selection.min_per_band || {})
        .map(([band, value]) => [band, Number(value) || 0])
        .filter(([, value]) => value > 0)
    ),
    include: (selection.include || []).filter(Boolean),
    exclude: (selection.exclude || []).filter(Boolean),
  };
}

export function isPoolPage(page) {
  return selectionOf(page) !== null;
}

/**
 * Le document `selection` d'une page qui bascule en tirage.
 *
 * Huit cases parce que la mosaïque n'affiche sa grille 2×4 qu'à exactement huit
 * cartes (MosaicPage.js) — en dessous, elle change de disposition.
 */
export function defaultSelection() {
  return { mode: "pool", count: 8, min_per_band: {}, include: [], exclude: [] };
}

export function poolEntriesOf(config) {
  const entries = config?.measure_pool?.entries;
  return Array.isArray(entries) ? entries.filter((e) => e && e.decision_id) : [];
}

export function poolIdsOf(config) {
  return [...new Set(poolEntriesOf(config).map((e) => e.decision_id))];
}

/**
 * LES MESURES QU'UNE PAGE PEUT PROPOSER, pour les vues d'ÉDITION.
 *
 * Sa liste figée, ou — si elle pioche — tout le réservoir. Sur-approximation
 * assumée, la même que côté serveur (`game_validator.page_decision_ids`) : on
 * ne sait pas laquelle sortira, donc toutes comptent. Équilibrer des impacts ou
 * relire des conséquences sur un seul tirage ne dirait rien de la page. Les
 * épinglées n'y ajoutent rien : elles se prennent DANS le réservoir, le tirage
 * ignore les autres.
 *
 * À NE PAS CONFONDRE AVEC `decisionIdsOf`, qui rend ce que la page ENREGISTRE.
 * Tout ce qui écrit doit continuer à passer par celle-là : un enregistrement
 * qui recopierait le réservoir dans `list_decisions` figerait la page.
 */
export function authoredDecisionIds(page, config) {
  const selection = selectionOf(page);
  const ids = (page?.content?.list_decisions || [])
    .map((d) => (typeof d === "string" ? d : d?._id))
    .filter(Boolean);
  if (!selection) return ids;
  return poolIdsOf(config);
}

export function bandsOf(config) {
  const bands = config?.measure_pool?.bands;
  if (!Array.isArray(bands) || bands.length === 0) return DEFAULT_BANDS;
  const clean = bands.filter((b) => b && b.id);
  if (clean.length === 0) return DEFAULT_BANDS;
  return [...clean].sort(
    (a, b) => (a.max_cost == null) - (b.max_cost == null) || (a.max_cost || 0) - (b.max_cost || 0)
  );
}

/** Doit rester identique à DEFAULT_BANDS de api/measure_pool.py. */
export const DEFAULT_BANDS = [
  { id: "faible", label: "Peu coûteuse", max_cost: 2 },
  { id: "moyen", label: "Modérée", max_cost: 5 },
  { id: "fort", label: "Coûteuse" },
];

/**
 * COMBIEN DE CASES CET ATELIER DEMANDE-T-IL AU RÉSERVOIR ?
 *
 * Le pire cas, celui qui décide si le réservoir tiendra : chaque mesure
 * proposée est prise, donc en sort. La pile est comptée avec ses répétitions —
 * une même page peut y figurer deux fois, et elle puise deux fois.
 */
export function slotsDemanded(config, pagesById) {
  return (config?.pile || []).reduce((total, pageId) => {
    const selection = selectionOf(pagesById[pageId]);
    return total + (selection ? selection.count : 0);
  }, 0);
}

/**
 * Un tirage d'exemple et la classification du réservoir, calculés par le
 * serveur sur le BROUILLON en cours — l'atelier et la page tels qu'ils sont à
 * l'écran, pas tels qu'ils sont en base. Sans quoi l'aperçu ne montrerait
 * jamais la modification qu'on vient de faire.
 */
export async function fetchPoolPreview(token, { config, page, decisions, seed, samples, taken }) {
  const res = await fetch(`${process.env.REACT_APP_API_URL}/pool_preview`, {
    method: "POST",
    headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" },
    body: JSON.stringify({
      config: config || {},
      page: page || {},
      decisions: decisions || [],
      seed: seed || "apercu",
      samples: samples || 0,
      taken: taken || [],
    }),
  });
  return res.json();
}
